import { Link, useLocation, useNavigate } from "react-router-dom";
import Brand from "./Brand.jsx";
import GatedLink from "./GatedLink.jsx";

const PUBLIC_PATHS = new Set([
    "/",
    "/protocol-health",
    // FAQ is a public page and gets the full marketing footer (not the
    // minimal app one), matching Home and Protocol Health.
    "/faq",
    "/404"
]);

const QUICK_LINKS = [
    { label: "Home", to: "/", kind: "link" },
    { label: "Protocol Health", to: "/protocol-health", kind: "link" },
    { label: "FAQ", to: "/faq", kind: "link" }
];

// Only ship links whose destination actually exists. Entries for pages not yet
// implemented in M1 are intentionally omitted rather than pointing at "/" (which
// silently lands the user on Home). This is especially important for legal pages
// (Privacy Policy / Terms) that carry compliance expectations.
const HELP_LINKS = [];

const ABOUT_LINKS = [];

// Social profiles render only when a real, live profile URL is supplied. Until a
// platform has a live profile, leave `href` null so the icon is not shown rather
// than rendering a dead `href="#"` that just scrolls to the top.
const SOCIAL_LINKS = [
    { label: "LinkedIn", href: null },
    { label: "Instagram", href: null },
    { label: "YouTube", href: null },
    { label: "Facebook", href: null },
    { label: "X", href: null }
];

const SOCIAL_ICONS = {
    LinkedIn: (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8.99h4.56V23H.22zM8.34 8.99h4.37v1.92h.06c.61-1.16 2.1-2.39 4.32-2.39 4.62 0 5.47 3.04 5.47 7v7.49h-4.55v-6.64c0-1.58-.03-3.62-2.21-3.62-2.21 0-2.55 1.72-2.55 3.5V23H8.34z" />
        </svg>
    ),
    Instagram: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="5" />
            <circle cx="12" cy="12" r="4" />
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
        </svg>
    ),
    YouTube: (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M23.5 7.5a3 3 0 0 0-2.1-2.1C19.5 5 12 5 12 5s-7.5 0-9.4.4A3 3 0 0 0 .5 7.5C.1 9.4.1 12 .1 12s0 2.6.4 4.5a3 3 0 0 0 2.1 2.1C4.5 19 12 19 12 19s7.5 0 9.4-.4a3 3 0 0 0 2.1-2.1c.4-1.9.4-4.5.4-4.5s0-2.6-.4-4.5zM9.6 15.5v-7l6.4 3.5z" />
        </svg>
    ),
    Facebook: (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M14 9h3V6h-3c-1.7 0-3 1.3-3 3v2H8v3h3v7h3v-7h2.5l.5-3H14V9z" />
        </svg>
    ),
    X: (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2H21l-6.515 7.443L22 22h-6.86l-4.78-6.249L4.8 22H2l6.96-7.951L1.5 2h7.018l4.32 5.708L18.244 2zm-2.41 18.4h1.9L8.25 3.508H6.213L15.834 20.4z" />
        </svg>
    )
};

/**
 * Footer rendered at the bottom of every page. Detects the current route to
 * decide between two layouts:
 *   - Public pages (Home, NotFound) get the marketing-grade footer with
 *     quick links columns, social icons, and the yellow copyright bar.
 *   - App pages (Dashboard etc.) get a single-line minimal footer.
 */
export default function Footer() {
    const location = useLocation();
    const isPublic = PUBLIC_PATHS.has(location.pathname);
    return isPublic ? <LandingFooter /> : <SimpleFooter />;
}

function SimpleFooter() {
    return (
        <footer className="border-t border-vb-border bg-vb-bg">
            <div className="mx-auto max-w-7xl px-4 py-6 text-xs text-vb-muted sm:px-8">
                <p>GILDer - All rights reserved.</p>
            </div>
        </footer>
    );
}

function LandingFooter() {
    return (
        <footer className="bg-vb-dark text-white">
            <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-8 sm:py-14 md:grid-cols-12 md:gap-10">
                <div className="md:col-span-4">
                    <Link to="/" aria-label="GILDer home" className="inline-block">
                        <Brand tone="light" size="lg" />
                    </Link>
                    <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/80">
                        GILDer is a premium, system-driven financial platform built on blockchain where users
                        invest in fixed-term bonds with protected capital and automated yield generation.
                    </p>
                    {SOCIAL_LINKS.some((s) => s.href) ? (
                        <div className="mt-6 flex items-center gap-3">
                            {SOCIAL_LINKS.filter((s) => s.href).map((social) => (
                                <SocialIcon key={social.label} label={social.label} href={social.href}>
                                    {SOCIAL_ICONS[social.label]}
                                </SocialIcon>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="md:col-span-5">
                    <h4 className="text-base font-bold text-white">Quick Links</h4>
                    <div
                        className={`mt-5 ${HELP_LINKS.length > 0 ? "grid grid-cols-2 gap-x-6" : ""} gap-y-3 text-sm`}
                    >
                        <ul className="space-y-3">
                            {QUICK_LINKS.map((link) => (
                                <li key={link.label}>
                                    <QuickLink link={link} />
                                </li>
                            ))}
                        </ul>
                        {HELP_LINKS.length > 0 ? (
                            <ul className="space-y-3">
                                {HELP_LINKS.map((link) => (
                                    <li key={link.label}>
                                        <Link className="text-white/85 hover:text-vb-accent transition-colors" to={link.to}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                </div>

                {ABOUT_LINKS.length > 0 ? (
                    <div className="md:col-span-3">
                        <h4 className="text-base font-bold text-white">About</h4>
                        <ul className="mt-5 space-y-3 text-sm">
                            {ABOUT_LINKS.map((link) => (
                                <li key={link.label}>
                                    <Link className="text-white/85 hover:text-vb-accent transition-colors" to={link.to}>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            </div>

            <div className="bg-vb-accent">
                <div className="mx-auto max-w-7xl px-4 py-3 text-center text-sm font-semibold text-vb-text sm:px-8">
                    &copy; 2026 GILDer. All rights reserved
                </div>
            </div>
        </footer>
    );
}

/**
 * Renders one footer Quick Link based on its `kind`:
 *   - "scrollTop": navigates home (if needed) and scrolls to the top.
 *   - "gated": uses GatedLink so disconnected users hit the wallet modal
 *     before landing on the destination.
 *   - "link" (default): plain react-router Link.
 */
function QuickLink({ link }) {
    const navigate = useNavigate();
    const location = useLocation();
    const className = "text-white/85 hover:text-vb-accent transition-colors";

    if (link.kind === "scrollTop") {
        function handleScrollTop(event) {
            event.preventDefault();
            if (location.pathname !== link.to) navigate(link.to);
            setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
        }
        return (
            <a href={link.to} className={className} onClick={handleScrollTop}>
                {link.label}
            </a>
        );
    }

    if (link.kind === "gated") {
        return (
            <GatedLink to={link.to} className={className}>
                {link.label}
            </GatedLink>
        );
    }

    return (
        <Link className={className} to={link.to}>
            {link.label}
        </Link>
    );
}

function SocialIcon({ label, href, children }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/10 text-white hover:bg-vb-accent hover:text-vb-text transition-colors"
        >
            <span className="h-4 w-4 inline-flex items-center justify-center">{children}</span>
        </a>
    );
}
